import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  closeNotificationsPanel,
  toggleNotificationsPanel
} from 'games/PredictionMarket/slice-thunk/predictionMarket.slice'
import { loadNotifications } from 'games/PredictionMarket/slice-thunk/predictionMarket.thunk'
import {
  formatNotificationTime,
  getNotificationIcon,
  getMarketDetailPath
} from 'games/PredictionMarket/predictionMarket.helpers'
import { ROUTE_PATHS } from 'constants/index'
import { BellSolidIcon } from 'components/ui-kit/Icons/svg'

const NotificationBell = ({ isLoggedIn, onToggle }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const containerRef = useRef(null)
  const { items, unreadCount, isOpen, loading } = useSelector((state) => state.predictionMarket.notifications)
  const { isDemoMode } = useSelector((state) => state.predictionMarket)

  useEffect(() => {
    if (isLoggedIn && isOpen && !isDemoMode) {
      dispatch(loadNotifications())
    }
  }, [isLoggedIn, isOpen, isDemoMode, dispatch])

  useEffect(() => {
    if (!isOpen) return undefined

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        dispatch(closeNotificationsPanel())
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, dispatch])

  if (!isLoggedIn) return null

  const handleNotificationClick = (notification) => {
    dispatch(closeNotificationsPanel())
    if (notification.questionId) {
      history.push(getMarketDetailPath({ marketId: notification.questionId }))
    }
  }

  const badgeLabel = unreadCount > 99 ? '99+' : unreadCount

  return (
    <div className='notification-bell' ref={containerRef}>
      <button
        type='button'
        className={`notification-bell__trigger ${isOpen ? 'notification-bell__trigger--active' : ''}`}
        onClick={() => {
          if (onToggle) {
            onToggle()
            return
          }
          dispatch(toggleNotificationsPanel())
        }}
        aria-label={t('notifications')}
        aria-expanded={isOpen}
      >
        <BellSolidIcon aria-hidden='true' />
        {unreadCount > 0 && (
          <span className='notification-bell__badge' aria-label={t('unreadNotifications', { count: unreadCount })}>
            {badgeLabel}
          </span>
        )}
      </button>

      {isOpen && (
        <div className='notification-bell__dropdown' role='dialog' aria-label={t('notifications')}>
          <div className='notification-bell__dropdown-header'>
            <h3>{t('notifications')}</h3>
            {items.length > 0 && (
              <span className='notification-bell__count'>{t('recentCount', { count: items.length })}</span>
            )}
          </div>

          <div className='notification-bell__list'>
            {loading && items.length === 0 ? (
              <p className='notification-bell__empty'>{t('loadingNotifications')}</p>
            ) : items.length === 0 ? (
              <p className='notification-bell__empty'>
                {t('noNotificationsYet')}
              </p>
            ) : (
              items.map((notification) => (
                <button
                  key={notification.id}
                  type='button'
                  className={`notification-bell__item ${notification.read ? '' : 'notification-bell__item--unread'}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <span className={`notification-bell__icon notification-bell__icon--${notification.category}`}>
                    {notification.category === 'order' && (
                      <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' className='h-4 w-4' aria-hidden='true'><path d='M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2'/><path d='M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z'/></svg>
                    )}
                    {notification.category === 'trade' && (
                      <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' className='h-4 w-4' aria-hidden='true'><path d='M7 16V4M7 4L3 8M7 4l4 4M17 8v12M17 20l4-4M17 20l-4-4'/></svg>
                    )}
                    {notification.category === 'order_complete' && (
                      <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' className='h-4 w-4' aria-hidden='true'><path d='M22 11.08V12a10 10 0 1 1-5.93-9.14'/><polyline points='22 4 12 14.01 9 11.01'/></svg>
                    )}
                    {notification.category === 'position' && (
                      <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' className='h-4 w-4' aria-hidden='true'><circle cx='12' cy='12' r='10'/><path d='M8 12h8M12 8v8'/></svg>
                    )}
                    {notification.category === 'resolution' && (
                      <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' className='h-4 w-4' aria-hidden='true'><path d='M14 13l5 5M4 21l5-5M9 4l5 5M12 11L7 6'/></svg>
                    )}
                    {notification.category === 'wallet' && (
                      <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' className='h-4 w-4' aria-hidden='true'><path d='M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z'/><path d='M16 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z'/><path d='M4 7V5a2 2 0 0 1 2-2h11'/></svg>
                    )}
                    {!['order', 'trade', 'order_complete', 'position', 'resolution', 'wallet'].includes(notification.category) && (
                      <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' className='h-4 w-4' aria-hidden='true'><path d='M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9'/><path d='M13.73 21a2 2 0 0 1-3.46 0'/></svg>
                    )}
                  </span>
                  <span className='notification-bell__content'>
                    <span className='notification-bell__title'>{notification.title}</span>
                    {notification.optionTitle && (
                      <span className='notification-bell__option'>{notification.optionTitle}</span>
                    )}
                    <span className='notification-bell__message'>{notification.message}</span>
                    <span className='notification-bell__time'>{formatNotificationTime(notification.createdAt)}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBell
